
import { useState, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/utils/firebase';
import { portfolioSchema, Portfolio as PortfolioDataFromZod } from '@/schemas/portfolio';

// Global locks to prevent simultaneous portfolio updates
const globalUpdateLocks = new Map<string, boolean>();
const lastPortfolioUpdateTimes = new Map<string, number>();
const MIN_UPDATE_INTERVAL = 30000; // 30 seconds

export const useSyncProtection = (portfolioId: string) => {
  const { toast } = useToast();
  
  const [isUpdatePending, setIsUpdatePending] = useState(false);
  // const [lastUpdateTime, setLastUpdateTime] = useState(0); // This state seems unused
  const updateLockRef = useRef(false);
  const lastSaveTimeRef = useRef(0);
  const isSavingRef = useRef(false);

  const handleSave = useCallback(async (e: React.MouseEvent<HTMLButtonElement> | undefined, name: string, initialName: string, setInitialName: (name: string) => void, pendingAction: any, executePendingAction: () => void) => {
    if (e) e.preventDefault();
    
    // Prevent if already saving
    if (isSavingRef.current) {
      console.log("Already saving, ignoring duplicate save request");
      toast({
        title: "Save in progress",
        description: "Please wait for the current save operation to complete."
      });
      return;
    }
    
    // Check global lock first
    if (globalUpdateLocks.get(portfolioId || '')) {
      console.log(`Global lock active for portfolio ${portfolioId}, skipping save`);
      toast({
        title: "Update in progress",
        description: "Please wait for the current update to complete."
      });
      return;
    }
    
    // Check throttling with longer interval
    const now = Date.now();
    const lastUpdateTime = lastPortfolioUpdateTimes.get(portfolioId || '') || 0;
    if (now - lastUpdateTime < MIN_UPDATE_INTERVAL) {
      console.log(`Too soon to update portfolio ${portfolioId}, throttling`);
      toast({
        title: "Please wait",
        description: "To prevent data conflicts, please wait before saving again."
      });
      return;
    }
    
    // Update last update time
    lastPortfolioUpdateTimes.set(portfolioId || '', now);
    
    // Set global lock
    globalUpdateLocks.set(portfolioId || '', true);
    
    // Prevent rapid multiple saves or loops
    if (isSavingRef.current || (now - lastSaveTimeRef.current < 60000)) { // Increased to 60 seconds
      console.log("Save operation throttled - skipping duplicate save");
      toast({
        title: "Already saving",
        description: "Your changes are being processed."
      });
      
      // Release global lock after a delay
      setTimeout(() => {
        globalUpdateLocks.set(portfolioId || '', false);
      }, 15000);
      
      return;
    }
    
    // Set saving state
    isSavingRef.current = true;
    lastSaveTimeRef.current = now;
    
    try {
      // Update the portfolio name
      if (portfolioId && name !== initialName) {
        console.log(`Saving portfolio name change from "${initialName}" to "${name}"`);
        
        const portfolioRef = doc(db, 'portfolios', portfolioId);
        const dataToUpdate: Partial<PortfolioDataFromZod> = { name };
        const validatedData = portfolioSchema.partial().pick({ name: true }).parse(dataToUpdate);
        
        await updateDoc(portfolioRef, {
          ...validatedData,
          updatedAt: serverTimestamp(),
        });
        
        // Update the initial name to match the new name to prevent further updates
        setInitialName(name);
      }
      
      toast({
        title: "Changes saved",
        description: "The portfolio information has been updated successfully."
      });
      
      if (pendingAction) {
        executePendingAction();
      }
    } catch (error) {
      console.error("Error saving portfolio:", error);
      toast({
        title: "Save error",
        description: "There was a problem saving your changes.",
        variant: "destructive"
      });
    } finally {
      // Reset saving state after a delay
      setTimeout(() => {
        isSavingRef.current = false;
        // Release global lock after a longer delay
        setTimeout(() => {
          globalUpdateLocks.set(portfolioId || '', false);
        }, 60000); // Extended to 60s
      }, 30000); // Extended to 30s
    }
  }, [portfolioId, toast]); // Removed updatePortfolio from dependencies
  
  const handleCancel = useCallback((setName: (name: string) => void, initialName: string, setFormEdited: (edited: boolean) => void, pendingAction: any, executePendingAction: () => void) => {
    // Reset the name
    setName(initialName);
    setFormEdited(false);
    
    if (pendingAction) {
      executePendingAction();
    }
  }, []);

  return {
    isUpdatePending,
    updateLockRef,
    handleSave,
    handleCancel
  };
};
